import { useEffect, useRef, useState, useCallback } from 'react';
import { FolderOpen } from 'lucide-react';
import { useCompany } from '@/contexts/CompanyContext';
import type { PurchaseRequest } from '@/types/purchase-request';
import { PurchaseRequestDocumentsTab } from './purchase-request-documents-tab';

const ADM_MFE_NAME = 'erp-softco-mfe-adm';
const STABLE_INSTANCE_ID = 'adm-pr-docs-panel';
const CALLBACK_EVENT_NAME = STABLE_INSTANCE_ID;

interface AdmMfeDocumentsPanelProps {
  purchaseRequest: PurchaseRequest;
}

/**
 * Mounts the Access Document Management MFE (erp-softco-mfe-adm) in panel
 * mode within the Purchase Request Documents tab.
 *
 * The MFE is mounted **once** with a stable instance ID and kept alive across
 * PR navigation. On subsequent PR changes only the document context is pushed
 * via `executeMfeFunctions('setDocumentContext', ...)` — no re-creation of the
 * MFE instance, so auth / discovery / backend resolution happen only once.
 */
export function AdmMfeDocumentsPanel({
  purchaseRequest,
}: AdmMfeDocumentsPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceIdRef = useRef<string | null>(null);
  const { currentCompany } = useCompany();
  const [mfeMounted, setMfeMounted] = useState(false);
  const [mountAttempted, setMountAttempted] = useState(false);

  const dbName = currentCompany?.financialsDatabaseName ?? '';

  // Keep a ref to the current PR so stable callbacks always read the latest.
  const purchaseRequestRef = useRef(purchaseRequest);
  purchaseRequestRef.current = purchaseRequest;

  const buildDocumentContext = useCallback(
    () => {
      const pr = purchaseRequestRef.current;
      const prPrimaryKey =
        pr.finOpsPrimaryKey != null
          ? String(pr.finOpsPrimaryKey)
          : pr.requestId || '';
      const accountCode = pr.supplier || '';

      return {
        linkType: 'PurchaseRequestTransaction',
        linkProduct: 'Access Dimensions',
        dbName,
        tags: JSON.stringify({
          'Database Name': dbName,
          'Purchase Request Primary': prPrimaryKey,
          'Account Code': accountCode,
        }),
        selectedRecordCount: prPrimaryKey ? '1' : '0',
      };
    },
    [dbName],
  );

  const pushContextViaExecute = useCallback(
    (retriesLeft = 2) => {
      const evoMfes = window.AccessEvo?.mfes;
      if (!evoMfes) return;

      const documentContext = buildDocumentContext();

      evoMfes
        .executeMfeFunctions(
          ADM_MFE_NAME,
          'setDocumentContext',
          documentContext,
          { timeout: 20000 },
        )
        .catch(() => {
          if (retriesLeft > 0) {
            setTimeout(() => pushContextViaExecute(retriesLeft - 1), 3000);
          }
        });
    },
    [buildDocumentContext],
  );

  // Mount the MFE once. The effect depends only on dbName (stable per company)
  // so navigating between PRs does NOT destroy/recreate the MFE.
  useEffect(() => {
    if (!dbName) return;

    const mount = () => {
      const container = containerRef.current;
      if (!container) return;

      const evoMfes = window.AccessEvo?.mfes;
      if (!evoMfes) {
        setMountAttempted(true);
        return;
      }

      instanceIdRef.current = STABLE_INSTANCE_ID;

      const documentContext = buildDocumentContext();

      evoMfes.add({
        name: ADM_MFE_NAME,
        instanceId: STABLE_INSTANCE_ID,
        container,
        shell: 'border-with-header-no-remove',
        enableFeedback: false,
        parameters: {
          mode: 'panel',
          callbackEventName: CALLBACK_EVENT_NAME,
          ...documentContext,
        },
      });

      // Push context immediately after add() so the host can buffer it
      // for delivery when the MFE mounts. The MFE checks for buffered
      // context during its mount phase — if we delay, we miss the window.
      pushContextViaExecute();

      setMfeMounted(true);
      setMountAttempted(true);
    };

    if (window.AccessEvo?.mfes) {
      mount();
    } else {
      // MFE host may still be initialising — poll briefly
      let attempts = 0;
      const maxAttempts = 10;
      const interval = setInterval(() => {
        attempts++;
        if (window.AccessEvo?.mfes) {
          clearInterval(interval);
          mount();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setMountAttempted(true);
        }
      }, 300);

      return () => {
        clearInterval(interval);
        if (instanceIdRef.current) {
          window.AccessEvo?.mfes?.remove(instanceIdRef.current);
          instanceIdRef.current = null;
          setMfeMounted(false);
          setMountAttempted(false);
        }
      };
    }

    return () => {
      if (instanceIdRef.current) {
        window.AccessEvo?.mfes?.remove(instanceIdRef.current);
        instanceIdRef.current = null;
        setMfeMounted(false);
        setMountAttempted(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbName]);

  // Push updated context whenever the purchase request changes.
  // The MFE stays mounted — only the document context is refreshed.
  useEffect(() => {
    if (!instanceIdRef.current || !dbName) return;
    pushContextViaExecute();
  }, [purchaseRequest.id, dbName, pushContextViaExecute]);

  // No database name configured — show fallback
  if (!dbName) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
          <FolderOpen className="h-7 w-7 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-medium">
            Document Management not configured
          </p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            This company does not have a linked database name configured.
            Documents will be available once ADM integration is set up for this
            instance.
          </p>
        </div>
      </div>
    );
  }

  // Evo MFE host not available after attempting — fall back to REST-based tab
  if (mountAttempted && !mfeMounted) {
    return (
      <PurchaseRequestDocumentsTab purchaseRequest={purchaseRequest} />
    );
  }

  return (
    <div
      ref={containerRef}
      id="adm-document-panel"
      className="w-full min-h-[200px]"
    />
  );
}
